'use client';

import { Checkbox, Typography } from '@musinsa/mds';
import { useDeviceInfo } from '@musinsa/packages-device-detection';
import { twMerge } from 'tailwind-merge';
import { getPaths, useIsDesktop } from '../../../../common';
import { SectionDivider } from '../shared/SectionDivider';
import { SEEDING_TERMS, type SeedingTermId, type SeedingTermItem } from './seedingTerms';

interface SeedingAgreementSectionProps {
  /** 항목별 체크 상태. */
  checked: Record<SeedingTermId, boolean>;
  /** "전체 동의하기" master 상태. */
  allChecked: boolean;
  /** 개별 항목 토글. */
  onToggle: (id: SeedingTermId) => void;
  /** 전체 동의 토글. */
  onToggleAll: () => void;
  /** "자세히" — 약관 시트 열기. */
  onOpenTerm: (id: SeedingTermId) => void;
  className?: string;
}

/**
 * 시딩 신청/수락 약관 동의 섹션 (controlled — 상태는 `useSeedingAgreement`).
 * 구조: 상단 Divider → 섹션 타이틀("캠페인 참여 동의") → "전체 동의하기" master →
 * 필수 4행(체크박스+자세히) → 안내 1행(자세히만).
 * `join/.../AgreementSection` 패턴 차용 — "자세히" 도 join 과 동일하게 desktop 웹은 새 탭,
 * 모바일/인앱 웹뷰는 바텀시트(onOpenTerm)로 분기한다. 안내 항목은 체크박스 없음(Figma node 2842-37419).
 */
export const SeedingAgreementSection = ({
  checked,
  allChecked,
  onToggle,
  onToggleAll,
  onOpenTerm,
  className,
}: SeedingAgreementSectionProps) => {
  const isDesktop = useIsDesktop();
  const { service } = useDeviceInfo();
  const paths = getPaths(service);

  // join 동일: desktop 웹 = 새 탭(목적별 styled 약관 페이지) / 모바일·웹뷰 = 바텀시트.
  const handleOpenTerm = (term: SeedingTermItem) => {
    if (isDesktop) {
      window.open(paths.seedingTerm({ termId: term.slug }), '_blank');
      return;
    }
    onOpenTerm(term.id);
  };

  return (
    <div className={twMerge('flex flex-col', className)}>
      <SectionDivider />
      {/* 간격은 항목이 아니라 컨테이너가 책임진다 — 행 padding 대신 gap-y, 가장자리는 컨테이너 padding.
        Figma 의 행별 py-8(타이틀/마스터/항목 공통)을 균일 gap-y-16 으로 환산. (.claude/rules/figma-list-spacing)
        순서상 뒤에 유의사항이 오므로(디자인 요청) 하단은 일반 섹션 여백 pb-12 — safe-area(pb-72)는
        마지막 요소인 유의사항이 책임. */}
      <div className="flex flex-col gap-y-[16px]! px-[16px]! pt-[12px]! pb-[12px]!">
        {/* 섹션 타이틀 — Figma node 2814-32133 (size 14 / medium). (SID-4592) */}
        <Typography size="14" weight="medium">
          캠페인 참여 동의
        </Typography>
        {/* 클릭 영역을 체크박스+라벨 전체로 확장. Checkbox 자체 onClick 제거하고 래퍼가 토글. (피드백 항목14) */}
        <div
          role="button"
          tabIndex={0}
          className="flex items-center gap-[8px]! cursor-pointer"
          onClick={onToggleAll}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              onToggleAll();
            }
          }}
        >
          <Checkbox checked={allChecked} />
          {/* 체크 시 medium/black, 미체크 시 regular(400)/gray-600. (SID-4654, Figma 신청전(모집중)_default) */}
          <Typography
            variant={allChecked ? 'body14pxMed' : 'body14pxReg'}
            colorScheme={allChecked ? 'black' : 'gray-600'}
          >
            전체 동의하기
          </Typography>
        </div>

        {SEEDING_TERMS.map((term) => (
          <div key={term.id} className="flex items-center justify-between gap-x-[12px]! pl-[24px]!">
            {term.required ? (
              <div
                role="button"
                tabIndex={0}
                className="flex items-center gap-[8px]! min-w-0 cursor-pointer"
                onClick={() => onToggle(term.id)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    onToggle(term.id);
                  }
                }}
              >
                <Checkbox checked={checked[term.id]} />
                {/* 체크 시 black / 미체크 시 gray-600. (SID-4592) */}
                <Typography
                  variant="body13pxReg"
                  colorScheme={checked[term.id] ? 'black' : 'gray-600'}
                >
                  {term.label}
                </Typography>
              </div>
            ) : (
              <div className="flex items-center gap-[8px]! min-w-0">
                {/* 안내(체크 없음) 라벨은 항상 black. (SID-4592) */}
                <Typography variant="body13pxReg" colorScheme="black">
                  {term.label}
                </Typography>
              </div>
            )}
            <Typography
              variant="body13pxReg"
              colorScheme="gray-600"
              className="shrink-0 cursor-pointer"
              textDecoration="underline"
              onClick={() => handleOpenTerm(term)}
            >
              자세히
            </Typography>
          </div>
        ))}
      </div>
    </div>
  );
};
